/**
 * Firestore helpers for the Business Context Library.
 * Collection: artifacts/{appId}/businessContextLibrary
 *
 * Items are reusable snippets about SMBPrime (mission, services, differentiators, etc.)
 * that are injected into the AI system prompt when generating blog articles.
 */

import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type DocumentData,
  type Timestamp,
} from 'firebase/firestore';
import { db, appId } from './firebase';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface BusinessContextItem {
  id: string;
  title: string;
  content: string;
  order: number;
  createdAt: Timestamp | Date;
  updatedAt: Timestamp | Date;
  createdBy?: string | null;
}

export interface BusinessContextItemPayload {
  title: string;
  content: string;
  order?: number;
}

// ---------------------------------------------------------------------------
// Ref helpers
// ---------------------------------------------------------------------------

const contextRef = () =>
  collection(db, 'artifacts', appId, 'businessContextLibrary');

const contextDocRef = (id: string) =>
  doc(db, 'artifacts', appId, 'businessContextLibrary', id);

function toItem(id: string, data: DocumentData): BusinessContextItem {
  return {
    id,
    title: data.title ?? '',
    content: data.content ?? '',
    order: data.order ?? 0,
    createdAt: data.createdAt ?? new Date(),
    updatedAt: data.updatedAt ?? new Date(),
    createdBy: data.createdBy ?? null,
  };
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

/** List all business context items ordered by `order` asc. */
export async function getAllBusinessContextItems(): Promise<BusinessContextItem[]> {
  const q = query(contextRef(), orderBy('order', 'asc'));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => toItem(d.id, d.data()));
}

/** Create a new business context item. Returns the new document id. */
export async function createBusinessContextItem(
  payload: BusinessContextItemPayload,
  createdBy: string
): Promise<string> {
  const docRefResult = await addDoc(contextRef(), {
    ...payload,
    order: payload.order ?? 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    createdBy,
  });
  return docRefResult.id;
}

/** Update an existing business context item. */
export async function updateBusinessContextItem(
  itemId: string,
  payload: Partial<BusinessContextItemPayload>,
  updatedBy: string
): Promise<void> {
  await setDoc(
    contextDocRef(itemId),
    { ...payload, updatedAt: serverTimestamp(), updatedBy },
    { merge: true }
  );
}

/** Delete a business context item. */
export async function deleteBusinessContextItem(itemId: string): Promise<void> {
  await deleteDoc(contextDocRef(itemId));
}

/** Batch-update `order` for a list of ids (used after drag-to-reorder or arrow reorder). */
export async function reorderBusinessContextItems(
  orderedIds: string[],
  updatedBy: string
): Promise<void> {
  await Promise.all(
    orderedIds.map((id, index) =>
      setDoc(
        contextDocRef(id),
        { order: index, updatedAt: serverTimestamp(), updatedBy },
        { merge: true }
      )
    )
  );
}

/**
 * Concatenate all business context items into a single plain-text block
 * suitable for injecting into an AI system or user prompt.
 */
export function buildBusinessContextText(items: BusinessContextItem[]): string {
  if (items.length === 0) return '';
  return items
    .map((item) => `## ${item.title}\n${item.content}`)
    .join('\n\n');
}

/**
 * Build a short business summary from context items for use in "Make me lucky"
 * and other prompts where a brief business blurb is needed. Truncates to
 * maxChars (default 600) so the pre-fill stays readable.
 */
export function buildShortBusinessSummary(
  items: BusinessContextItem[],
  maxChars: number = 600
): string {
  if (items.length === 0) return '';
  const full = items
    .map((item) => `${item.title}: ${item.content.replace(/\n/g, ' ').trim()}`)
    .join(' ');
  const trimmed = full.replace(/\s+/g, ' ').trim();
  if (trimmed.length <= maxChars) return trimmed;
  const cut = trimmed.slice(0, maxChars - 1).trim();
  const lastSpace = cut.lastIndexOf(' ');
  const end = lastSpace > maxChars * 0.6 ? lastSpace : cut.length;
  return cut.slice(0, end) + '…';
}

// ---------------------------------------------------------------------------
// Default SMBPrime context (seed from admin UI — no service account needed)
// ---------------------------------------------------------------------------

export const DEFAULT_SMBPRIME_CONTEXT_ITEMS: Array<{
  id: string;
  title: string;
  content: string;
  order: number;
}> = [
  {
    id: 'mission',
    title: 'Mission Statement',
    content: `SMBPrime is a federal government contracting (GovCon) advisory and business development platform for small and mid-size businesses (SMBs). Our mission is to demystify federal contracting and help SMBs grow through smart BD strategy, compliance readiness, and strategic teaming. We focus on compliance-first growth, BD pipeline acceleration, SAM.gov and entity management expertise, and NAICS code optimization so that our clients can compete and win in the federal market.`,
    order: 0,
  },
  {
    id: 'key-services',
    title: 'Key Services',
    content: `SMBPrime offers:
- Federal market entry and growth strategy for small and mid-size contractors
- SAM.gov registration, maintenance, and entity management guidance
- NAICS code selection and optimization for better opportunity matching
- BD pipeline development and capture support
- Teaming and subcontracting strategy
- Compliance readiness (e.g., 8(a), HUBZone, SDVOSB, WOSB) and ongoing eligibility
- Past performance and CPARS awareness
- GSA Schedule and other contract vehicle considerations
- Proposal and capture support where applicable`,
    order: 1,
  },
  {
    id: 'differentiators',
    title: 'Differentiators',
    content: `What sets SMBPrime apart:
- Deep focus on SMBs ($1M–$50M range) and companies new to federal contracting, not enterprise-only
- Practical, actionable guidance—we emphasize what to do next, not just theory
- Compliance-first approach: staying eligible and audit-ready while pursuing growth
- SAM.gov and UEI/CAGE/NAICS expertise so clients show up correctly in systems and searches
- Understanding of both prime and subcontractor paths and how they intersect
- Emphasis on strategic teaming and positioning, not just bidding on every opportunity`,
    order: 2,
  },
  {
    id: 'target-audience',
    title: 'Target Audience',
    content: `SMBPrime serves:
- Small and mid-size businesses (roughly $1M–$50M in revenue) pursuing or already in federal contracting
- Prime contractors looking to strengthen BD, compliance, and subcontractor strategy
- Subcontractors seeking to work with primes and understand federal requirements
- Companies new to federal contracting that need to get SAM.gov ready, choose NAICS codes, and build a BD pipeline
- Business development and capture professionals at SMBs
- Owners and executives who want a trusted advisor for GovCon strategy and compliance`,
    order: 3,
  },
  {
    id: 'tone-and-voice',
    title: 'Tone & Voice Guidelines',
    content: `When writing for SMBPrime:
- Professional and authoritative: we are the trusted advisor, not a generic vendor
- Practical and actionable: give readers clear next steps and specific guidance where possible
- Never salesy or hyperbolic: avoid vague marketing speak and overpromising
- Use real GovCon concepts and terms: NAICS, SAM.gov, UEI, CAGE, set-asides, 8(a), HUBZone, SDVOSB, past performance, CPARS, teaming, capture, BD pipeline
- Speak to federal contractors and small business owners with respect for their time and goals
- Be concise; use headings and lists for scannability. Prefer concrete examples over abstract claims.`,
    order: 4,
  },
  {
    id: 'insights-article-standard',
    title: 'Insights Article Standard',
    content: `Blog articles on the Insights page must match this standard:

- Positioning: "Practical insights on federal subcontracting, Prime outreach, and structured growth."

- Topics we cover: Federal Subcontracting Strategy, Prime Contractor Outreach, BD Process & Systems, Small Business Growth, NAICS Alignment, Meeting Preparation. Every article should align with at least one of these.

- Every article must include: (1) Practical Insights — real strategies readers can apply to their federal BD process immediately; (2) Growth Focus — insights aligned with structured, sustainable growth, not quick wins; (3) Clear Next Steps — every article ends with a direct path forward (e.g. our process or a strategy call).`,
    order: 5,
  },
  {
    id: 'govcon-basics',
    title: 'GovCon Basics (for accuracy)',
    content: `Use these accurately in content:
- SAM.gov: System for Award Management; required for federal contracting; includes UEI (Unique Entity ID), CAGE code, NAICS codes, and representations/certifications
- NAICS: North American Industry Classification System; 2-, 4-, or 6-digit codes used for matching and set-asides
- Set-asides: small business, 8(a), HUBZone, SDVOSB, WOSB, etc.
- Teaming: prime-sub relationships, joint ventures, and mentor-protégé where relevant
- BD pipeline: opportunities in various stages from identification to proposal
- Past performance / CPARS: Contractor Performance Assessment Reporting System; matters for evaluation
- GSA Schedules: one common vehicle for federal sales; not the only path`,
    order: 6,
  },
];

/**
 * Seed or update the default SMBPrime context items in Firestore.
 * Call from the admin UI (no service account needed). Idempotent: re-running merges into existing docs.
 */
export async function seedDefaultSMBPrimeContext(createdBy: string): Promise<void> {
  const ts = serverTimestamp();
  for (const item of DEFAULT_SMBPRIME_CONTEXT_ITEMS) {
    await setDoc(
      contextDocRef(item.id),
      {
        title: item.title,
        content: item.content,
        order: item.order,
        createdAt: ts,
        updatedAt: ts,
        createdBy,
      },
      { merge: true }
    );
  }
}
